import { useEffect, useRef } from "react";
import { SupportChat } from "./SupportChat.tsx";
import { closeSupport, useSupportView } from "./supportCenter.ts";

/**
 * The practice app the coach is in — its id is the first path segment
 * ("/wr/…" → "wr"). Tickets carry it, so the support email names the app.
 */
const currentAppId = (): string | undefined => {
  if (typeof window === "undefined") return undefined;
  return window.location.pathname.split("/")[1] || undefined;
};

/**
 * Focus moves into the chat as it opens — keys go to it, not to the page
 * behind (the drawer row that opened it is gone) — and back as it closes.
 */
function useFocusWhileOpen(open: boolean) {
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    const before = document.activeElement;
    // Without scrolling: the chat's wrapper sits after the app, and focusing
    // it scrolled the page behind to the bottom (client 2026-10-09).
    ref.current?.focus({ preventScroll: true });
    return () => {
      if (before instanceof HTMLElement && before.isConnected) {
        before.focus({ preventScroll: true });
      }
    };
  }, [open]);
  return ref;
}

/**
 * The support chat for the practice apps (in-game parity, client
 * 2026-10-08), ported from the in-game app. Mounted once at the dashboard
 * root and opened from the drawer's Help and Support menu (see
 * supportCenter.ts). The FAQ and Contact Us are pages of each app, under its
 * own nav bar (client 2026-10-12); the chat is a panel over whatever page is
 * open, as in-game. Above the app; under the drawer, its menus and the
 * dialogs, which close or sit over it.
 */
export function SupportCenter() {
  const view = useSupportView();
  const chatRef = useFocusWhileOpen(view === "chat");
  // Back (an iPad edge swipe) closes the chat rather than leave it over a
  // different page.
  useEffect(() => {
    if (!view) return;
    window.addEventListener("popstate", closeSupport);
    return () => window.removeEventListener("popstate", closeSupport);
  }, [view]);
  return (
    <div className="relative z-[99980]">
      {/* Always mounted (it renders nothing while closed), as in-game: an
          answer that arrives after the coach closed the chat is kept, and so
          is a half-typed question. A dialog only while open, so the "Update
          Available!" prompt (and Special Teams' Enter-to-add) wait for it. */}
      <div
        ref={chatRef}
        role={view === "chat" ? "dialog" : undefined}
        aria-modal={view === "chat" ? true : undefined}
        aria-label="Chat Support"
        tabIndex={view === "chat" ? -1 : undefined}
        className="outline-none"
      >
        <SupportChat
          open={view === "chat"}
          onOpenChange={(open) => {
            if (!open) closeSupport();
          }}
          getAppKey={currentAppId}
        />
      </div>
    </div>
  );
}
